<div class="qc-container">
  <div class="loading-container" *ngIf="loading">
    <mat-spinner diameter="36"></mat-spinner>
  </div>

  <div class="qc-toolbar">
    <div class="qc-filter-group">
      <label for="qc-event-type-filter">Event Type</label>
      <select
        id="qc-event-type-filter"
        class="qc-native-select"
        [(ngModel)]="eventCodeFilter"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="onEventCodeFilterChanged($event)"
      >
        <option value="">All events</option>
        <option *ngFor="let eventCode of eventCodeOptions" [value]="eventCode">
          {{ eventCode }}
        </option>
      </select>
    </div>

    <div class="qc-filter-group">
      <label for="qc-league-filter">League</label>
      <input
        id="qc-league-filter"
        type="text"
        class="qc-filter-input"
        [ngModel]="leagueFilter"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="onLeagueFilterChanged($event)"
        placeholder="e.g. NL"
      />
    </div>

    <div class="qc-filter-group">
      <label for="qc-game-filter">Game</label>
      <input
        id="qc-game-filter"
        type="text"
        class="qc-filter-input"
        [ngModel]="gameFilter"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="onGameFilterChanged($event)"
        placeholder="Home - Away"
      />
    </div>

    <div class="qc-filter-group">
      <label for="qc-dc-date-from-filter">DC Date</label>
      <div class="qc-date-range-inputs">
        <input
          id="qc-dc-date-from-filter"
          type="date"
          class="qc-filter-input qc-date-range-input"
          [ngModel]="dcDateFromFilter"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="onDcDateFromFilterChanged($event)"
          aria-label="DC date from"
        />
        <span class="qc-date-range-separator">to</span>
        <input
          id="qc-dc-date-to-filter"
          type="date"
          class="qc-filter-input qc-date-range-input"
          [ngModel]="dcDateToFilter"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="onDcDateToFilterChanged($event)"
          aria-label="DC date to"
        />
      </div>
    </div>

    <div class="qc-filter-group qc-collector-filter">
      <label>Collector</label>
      <app-collector-input
        class="qc-collector-input"
        [auth0Id]="collectorAuthIdFilter"
        [displayName]="collectorDirectory.get(collectorAuthIdFilter)?.name ?? ''"
        (auth0IdChange)="onCollectorFilterChanged($event)"
      ></app-collector-input>
    </div>

    <mat-checkbox
      class="show-analyzed-checkbox"
      [checked]="showAnalyzed"
      (change)="onShowAnalyzedChanged($event.checked)"
    >
      Show analyzed
    </mat-checkbox>

    <div class="qc-count">{{ visibleEventCount }} of {{ totalCount }} events</div>
  </div>

  <table
    mat-table
    [dataSource]="events"
    [trackBy]="trackByEventId"
    class="qc-table mat-elevation-z2"
  >
    <ng-container matColumnDef="actions">
      <th mat-header-cell *matHeaderCellDef class="actions-column"></th>
      <td mat-cell *matCellDef="let event" class="actions-column">
        <button
          mat-icon-button
          type="button"
          class="open-dialog-button"
          (click)="openQcDialog(event)"
          aria-label="Open QC panel"
        >
          <mat-icon class="open-dialog-icon">open_in_full</mat-icon>
        </button>
      </td>
    </ng-container>

    <ng-container matColumnDef="status">
      <th mat-header-cell *matHeaderCellDef>Status</th>
      <td mat-cell *matCellDef="let event">
        <span
          class="state-pill"
          [ngClass]="{
            'state-done': event.qc?.status === 'done',
            'state-pending': event.qc?.status !== 'done'
          }"
        >
          {{ getStatusLabel(event) }}
        </span>
      </td>
    </ng-container>

    <ng-container matColumnDef="dcType">
      <th mat-header-cell *matHeaderCellDef>DC Type</th>
      <td mat-cell *matCellDef="let event">{{ event.dcType ?? '—' }}</td>
    </ng-container>

    <ng-container matColumnDef="league">
      <th mat-header-cell *matHeaderCellDef>League</th>
      <td mat-cell *matCellDef="let event">{{ event.league ?? '—' }}</td>
    </ng-container>

    <ng-container matColumnDef="dcDate">
      <th mat-header-cell *matHeaderCellDef>DC Date</th>
      <td mat-cell *matCellDef="let event">{{ event.dcDate ?? '—' }}</td>
    </ng-container>

    <ng-container matColumnDef="gameDate">
      <th mat-header-cell *matHeaderCellDef>Game Date</th>
      <td mat-cell *matCellDef="let event">
        {{ event.gameDateTime ? (event.gameDateTime | date : 'yyyy-MM-dd') : '—' }}
      </td>
    </ng-container>

    <ng-container matColumnDef="game">
      <th mat-header-cell *matHeaderCellDef>Game</th>
      <td mat-cell *matCellDef="let event">{{ getGameLabel(event) }}</td>
    </ng-container>

    <ng-container matColumnDef="time">
      <th mat-header-cell *matHeaderCellDef>Time</th>
      <td mat-cell *matCellDef="let event">
        {{ event.gameDateTime ? (event.gameDateTime | date : 'HH:mm') : '—' }}
      </td>
    </ng-container>

    <ng-container matColumnDef="event">
      <th mat-header-cell *matHeaderCellDef>Event</th>
      <td mat-cell *matCellDef="let event">{{ event.code }}</td>
    </ng-container>

    <ng-container matColumnDef="collector">
      <th mat-header-cell *matHeaderCellDef>Collector</th>
      <td mat-cell *matCellDef="let event">
        <div class="collector-name">{{ getCollectorName(event) }}</div>
        <div class="collector-email">{{ getCollectorEmail(event) }}</div>
      </td>
    </ng-container>

    <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
    <tr
      mat-row
      *matRowDef="let event; let index = index; columns: displayedColumns"
      class="qc-row"
      [class.alt-qc-row]="index % 2 === 1"
    ></tr>

    <tr class="mat-row" *matNoDataRow>
      <td class="mat-cell empty-row" [attr.colspan]="displayedColumns.length">
        No QC events found.
      </td>
    </tr>
  </table>

  <div class="qc-footer">
    <mat-paginator
      [length]="totalCount"
      [pageSize]="pageSize"
      [pageIndex]="pageIndex"
      [pageSizeOptions]="pageSizeOptions"
      (page)="onPageChanged($event)"
    ></mat-paginator>
  </div>
</div>
